import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const MealPlanContext = createContext();

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SLOTS = ['breakfast', 'lunch', 'dinner', 'snack'];
const STORAGE_KEY = 'dfwfitmeals_mealplan';

function createEmptyPlan() {
  const plan = {};
  DAYS.forEach(day => {
    plan[day] = {};
    SLOTS.forEach(slot => { plan[day][slot] = null; });
  });
  return plan;
}

export function MealPlanProvider({ children }) {
  const [plan, setPlan] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return createEmptyPlan();
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
  }, [plan]);

  const addMeal = useCallback((day, slot, product) => {
    setPlan(prev => ({
      ...prev,
      [day]: { ...prev[day], [slot]: product },
    }));
  }, []);

  const removeMeal = useCallback((day, slot) => {
    setPlan(prev => ({
      ...prev,
      [day]: { ...prev[day], [slot]: null },
    }));
  }, []);

  const clearPlan = useCallback(() => {
    setPlan(createEmptyPlan());
  }, []);

  const getMealCount = useCallback(() => {
    let count = 0;
    DAYS.forEach(day => SLOTS.forEach(slot => { if (plan[day]?.[slot]) count++; }));
    return count;
  }, [plan]);

  const getTotals = useCallback(() => {
    const totals = { calories: 0, protein: 0, carbs: 0, fat: 0, meals: 0 };
    DAYS.forEach(day => {
      SLOTS.forEach(slot => {
        const m = plan[day]?.[slot];
        if (m) {
          totals.calories += m.calories || 0;
          totals.protein += m.protein || 0;
          totals.carbs += m.carbs || 0;
          totals.fat += m.fat || 0;
          totals.meals += 1;
        }
      });
    });
    return totals;
  }, [plan]);

  const getDayTotals = useCallback((day) => {
    const totals = { calories: 0, protein: 0, carbs: 0, fat: 0, meals: 0 };
    SLOTS.forEach(slot => {
      const m = plan[day]?.[slot];
      if (m) {
        totals.calories += m.calories || 0;
        totals.protein += m.protein || 0;
        totals.carbs += m.carbs || 0;
        totals.fat += m.fat || 0;
        totals.meals += 1;
      }
    });
    return totals;
  }, [plan]);

  const getShareData = useCallback(() => {
    const meals = [];
    DAYS.forEach(day => {
      SLOTS.forEach(slot => {
        const m = plan[day]?.[slot];
        if (m) meals.push({ d: day.slice(0, 3), s: slot.slice(0, 1), n: m.name, sl: m.slug });
      });
    });
    return btoa(JSON.stringify(meals));
  }, [plan]);

  const loadFromShare = useCallback((encoded) => {
    try {
      const meals = JSON.parse(atob(encoded));
      // We can't restore full product objects from just names — return the decode for display
      return meals;
    } catch { return null; }
  }, []);

  return (
    <MealPlanContext.Provider value={{
      plan, addMeal, removeMeal, clearPlan, getMealCount, getTotals, getDayTotals,
      getShareData, loadFromShare, DAYS, SLOTS,
    }}>
      {children}
    </MealPlanContext.Provider>
  );
}

export function useMealPlan() {
  const ctx = useContext(MealPlanContext);
  if (!ctx) throw new Error('useMealPlan must be used within MealPlanProvider');
  return ctx;
}

export { DAYS, SLOTS };
